import React, { useEffect, useRef, useCallback } from "react";
import { useAntigravityResize } from "@/hooks/useAntigravityResize";
import { useTouchCoordinates } from "@/hooks/useTouchCoordinates";

/**
 * AntigravityCanvas
 * Contenedor adaptativo de alto rendimiento para Antigravity 2.5.5.
 * Incluye fallback de simulación física de partículas interactiva si la librería no está inyectada globalmente.
 */
export const AntigravityCanvas = React.memo(function AntigravityCanvas({
  className = "",
  variant = "subtle", // "subtle" | "interactive" | "hero"
  interactive = true,
}) {
  const engineRef = useRef(null);
  const animFrameRef = useRef(null);
  const pointerRef = useRef({ x: -1000, y: -1000, active: false });
  const isMobileRef = useRef(false);

  // Callback de Resize sin desmontar componentes
  const handleResize = useCallback((metrics) => {
    isMobileRef.current = metrics.isMobile;

    if (engineRef.current?.resize) {
      engineRef.current.resize(metrics.width, metrics.height, metrics.dpr);
      if (metrics.isMobile && engineRef.current.setQuality) {
        engineRef.current.setQuality({ particleScale: 0.5, substeps: 1 });
      } else if (engineRef.current.setQuality) {
        engineRef.current.setQuality({ particleScale: 1.0, substeps: 2 });
      }
    }
  }, []);

  const { containerRef, canvasRef } = useAntigravityResize(handleResize);

  // Manejo de puntero y gestos táctiles
  const handlePointerUpdate = useCallback((pointerData) => {
    pointerRef.current = pointerData;
    if (engineRef.current?.setPointer) {
      engineRef.current.setPointer(pointerData.x, pointerData.y, pointerData.active);
    }
  }, []);

  useTouchCoordinates(canvasRef, handlePointerUpdate);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let destroyed = false;

    // Inicializar Antigravity 2.5.5 si está disponible en window
    if (typeof window !== "undefined" && window.Antigravity) {
      try {
        engineRef.current = new window.Antigravity({
          canvas,
          variant,
          interactive,
          powerPreference: "high-performance",
        });
        engineRef.current.start?.();
        return () => {
          destroyed = true;
          engineRef.current?.destroy?.();
        };
      } catch (e) {
        console.warn("Fallo al inicializar window.Antigravity, usando fallback nativo:", e);
      }
    }

    // Fallback nativo: Motor de partículas antigravitacionales interactivo 60FPS
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const particleCount = isMobileRef.current ? 35 : 75;
    const particles = Array.from({ length: particleCount }, () => ({
      x: Math.random() * (canvas.width || 800),
      y: Math.random() * (canvas.height || 600),
      vx: (Math.random() - 0.5) * 0.6,
      vy: (Math.random() - 0.5) * 0.6 - 0.2, // Flotación hacia arriba (antigravedad)
      radius: Math.random() * 2 + 1,
      alpha: Math.random() * 0.4 + 0.1,
      baseAlpha: Math.random() * 0.4 + 0.1,
    }));

    const render = () => {
      if (destroyed) return;
      const w = canvas.width;
      const h = canvas.height;

      ctx.clearRect(0, 0, w, h);

      const pointer = pointerRef.current;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;

        // Rebote en bordes
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;

        // Físicas interactivas con cursor o touch
        if (pointer.active) {
          const dx = pointer.x * (canvas.width / (canvas.clientWidth || 1)) - p.x;
          const dy = pointer.y * (canvas.height / (canvas.clientHeight || 1)) - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const maxDist = isMobileRef.current ? 100 : 160;

          if (dist < maxDist) {
            const force = (1 - dist / maxDist) * 1.5;
            p.x -= (dx / dist) * force * 3;
            p.y -= (dy / dist) * force * 3;
            p.alpha = Math.min(1, p.baseAlpha + 0.4);
          } else {
            p.alpha = p.baseAlpha;
          }
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(99, 102, 241, ${p.alpha})`;
        ctx.fill();

        // Conexiones de red antigravitacional
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dist2 = Math.hypot(p.x - p2.x, p.y - p2.y);
          const maxLine = isMobileRef.current ? 70 : 110;

          if (dist2 < maxLine) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(148, 163, 184, ${(1 - dist2 / maxLine) * 0.15})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      }

      animFrameRef.current = requestAnimationFrame(render);
    };

    animFrameRef.current = requestAnimationFrame(render);

    return () => {
      destroyed = true;
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [variant, interactive]);

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full overflow-hidden ${className}`}
    >
      <canvas
        ref={canvasRef}
        className="w-full h-full block will-change-transform"
        style={{ touchAction: interactive ? "none" : "auto" }}
      />
    </div>
  );
});
